import { useState } from 'react'
import { toast } from 'sonner'
import {
  downloadTransactionsCsv,
  downloadAccountsCsv,
  downloadGoalsCsv,
  downloadFullExport,
} from '@/api/export'
import { Button } from '@/components/ui/button'
import { SettingsPanel, SettingsRow, ROW_BUTTON } from './SettingsPanel'

type ExportKind = 'transactions' | 'accounts' | 'goals' | 'full'

const EXPORTS: {
  kind: ExportKind
  title: string
  description: string
  format: string
  download: () => Promise<void>
}[] = [
  {
    kind: 'transactions',
    title: 'Transactions',
    description: 'Every income, expense, and transfer, as a spreadsheet.',
    format: 'CSV',
    download: downloadTransactionsCsv,
  },
  {
    kind: 'accounts',
    title: 'Accounts',
    description: 'All accounts, including archived ones, with current balances.',
    format: 'CSV',
    download: downloadAccountsCsv,
  },
  {
    kind: 'goals',
    title: 'Goals',
    description: 'All savings goals with progress and status.',
    format: 'CSV',
    download: downloadGoalsCsv,
  },
  {
    kind: 'full',
    title: 'Everything',
    description: 'A complete, machine-readable copy of your data, plus your own profile.',
    format: 'JSON',
    download: downloadFullExport,
  },
]

export function DataPanel() {
  const [downloading, setDownloading] = useState<ExportKind | null>(null)

  async function handleDownload(item: (typeof EXPORTS)[number]) {
    setDownloading(item.kind)
    try {
      await item.download()
    } catch {
      toast.error(`Failed to export ${item.title.toLowerCase()}. Please try again.`)
    } finally {
      setDownloading(null)
    }
  }

  return (
    <SettingsPanel id="data" title="Your data" aside="Download a copy any time">
      {EXPORTS.map((item) => (
        <SettingsRow
          key={item.kind}
          label={
            <>
              {item.title}{' '}
              <span className="text-xs font-normal text-[var(--fl-text-3)]">{item.format}</span>
            </>
          }
          hint={item.description}
        >
          <Button
            variant="outline"
            className={ROW_BUTTON}
            aria-label={`Download ${item.title.toLowerCase()} ${item.format}`}
            onClick={() => handleDownload(item)}
            disabled={downloading === item.kind}
          >
            {downloading === item.kind ? 'Downloading…' : 'Download'}
          </Button>
        </SettingsRow>
      ))}
    </SettingsPanel>
  )
}
